/* Reusable creator sections. Custom elements also initialize after editor reloads. */
if (!customElements.get('hbr-creator-parts-library')) {
  customElements.define('hbr-creator-parts-library', class extends HTMLElement {
    connectedCallback() {
      this.parts = this.querySelector('hbr-creator-parts');
      if (!this.parts) return;
      this.home = this.parts.parentElement;
      this.events = new AbortController();
      const options = {signal: this.events.signal};
      document.addEventListener('shopify:section:load', () => this.mount(), options);
      document.addEventListener('shopify:section:unload', event => {
        if (event.target.contains(this.parts) && !event.target.contains(this)) this.restore();
      }, options);
      document.addEventListener('DOMContentLoaded', () => this.mount(), options);
      this.mount();
    }
    mount() {
      const garage = [...document.querySelectorAll('hbr-creator-garage')].find(item => item.dataset.garageKey === this.dataset.garageKey);
      const panel = [...(garage?.querySelectorAll('[data-build-panel]') || [])].find(item => item.dataset.buildKey === this.dataset.buildKey);
      if (panel?.contains(this.parts)) return;
      this.restore();
      // Never replace existing cards or metaobject content with an overflow library.
      const fallback = panel?.querySelector('[data-parts-fallback]');
      if (!fallback || panel.querySelector('hbr-creator-parts')) return;
      this.fallback = fallback;
      fallback.hidden = true;
      fallback.after(this.parts);
      this.hidden = true;
    }
    restore() {
      if (this.fallback) this.fallback.hidden = false;
      this.fallback = null;
      if (this.parts && this.home && this.parts.parentElement !== this.home) this.home.append(this.parts);
      this.hidden = false;
    }
    disconnectedCallback() { this.events?.abort(); this.restore(); }
  });
}
if (!customElements.get('hbr-creator-video')) {
  customElements.define('hbr-creator-video', class extends HTMLElement {
    static loadAPI() {
      if (window.YT?.Player) return Promise.resolve(window.YT);
      if (!this.apiPromise) {
        this.apiPromise = new Promise((resolve, reject) => {
          const previous = window.onYouTubeIframeAPIReady;
          window.onYouTubeIframeAPIReady = () => {
            resolve(window.YT);
            previous?.();
          };
          const script = document.createElement('script');
          script.src = 'https://www.youtube.com/iframe_api';
          script.onerror = () => { this.apiPromise = null; reject(new Error('YouTube API unavailable')); };
          document.head.append(script);
        });
      }
      return this.apiPromise;
    }
    connectedCallback() {
      this.onOtherPlayback = event => { if (event.detail !== this) this.pause(); };
      document.addEventListener('hbr:video-play', this.onOtherPlayback);
      if (this.ready) return;
      this.ready = true;
      this.querySelector('button')?.addEventListener('click', () => this.playAt(0));
    }
    disconnectedCallback() {
      document.removeEventListener('hbr:video-play', this.onOtherPlayback);
      this.pause();
    }
    pause() {
      this.playRequested = false;
      if (this.playerReady) this.player.pauseVideo();
      else {
        const frame = this.querySelector('iframe');
        if (frame?.src.includes('autoplay=1')) frame.src = frame.src.replace('autoplay=1', 'autoplay=0');
      }
    }
    playAt(seconds = 0) {
        const id = this.dataset.videoId;
        if (!/^[\w-]{11}$/.test(id || '')) return;
        this.startSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
        this.playRequested = true;
        document.dispatchEvent(new CustomEvent('hbr:video-play', {detail: this}));
        if (this.playerReady) {
          this.player.seekTo(this.startSeconds, true);
          this.player.unMute();
          this.player.playVideo();
          return;
        }
        if (this.querySelector('iframe')) return;
        const frame = document.createElement('iframe');
        frame.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&start=${this.startSeconds}&playsinline=1&enablejsapi=1&cc_load_policy=0&rel=0&origin=${encodeURIComponent(location.origin)}`;
        frame.title = this.dataset.title || 'Build video';
        frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        frame.allowFullscreen = true;
        frame.referrerPolicy = 'strict-origin-when-cross-origin';
        this.querySelector('.hbr-creator__video-screen').replaceChildren(frame);
        frame.focus();
        // Request playback when the player is ready, not just when its URL loads.
        this.constructor.loadAPI().then(YT => {
          if (!frame.isConnected) return;
          let retriedMuted = false;
          this.player = new YT.Player(frame, {
            events: {
              onReady: event => {
                this.playerReady = true;
                if (this.playRequested && !this.closest('[hidden]')) {
                  event.target.seekTo(this.startSeconds, true);
                  event.target.unMute();
                  event.target.playVideo();
                } else event.target.pauseVideo();
              }
            }
          });
        }).catch(() => { /* The native player remains usable if the API is blocked. */ });
    }
  });
}
if (!customElements.get('hbr-creator-parts')) {
  customElements.define('hbr-creator-parts', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.events = new AbortController();
      const options = {signal: this.events.signal};
      this.cards = [...this.querySelectorAll('[data-part-card]')];
      this.filters = this.querySelector('[data-part-filters]');
      this.filters.querySelectorAll('button:not(:first-child)').forEach(button => button.remove());
      this.more = this.querySelector('[data-part-more]');
      this.dialog = this.querySelector('dialog');
      this.category = '';
      this.expanded = false;
      this.mobile = matchMedia('(max-width: 749px)');
      this.mobile.addEventListener('change', () => this.filter(), options);
      [...new Set(this.cards.map(card => card.dataset.category))].sort().forEach(category => {
        const button = document.createElement('button');
        button.type = 'button'; button.dataset.category = category;
        button.textContent = category; button.setAttribute('aria-pressed', 'false');
        this.filters.append(button);
      });
      this.querySelector('.hbr-parts__tools').hidden = false;
      this.filters.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        this.category = button.dataset.category; this.expanded = false; this.filter();
      }, options);
      this.more.addEventListener('click', () => {
        this.expanded = !this.expanded; this.filter();
        if (!this.expanded) this.querySelector('.hbr-parts__intro').scrollIntoView({block: 'start', behavior: 'auto'});
      }, options);
      this.addEventListener('click', event => {
        const moment = event.target.closest('[data-part-moment]');
        if (!moment || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || !this.dialog.showModal || !/^[\w-]{11}$/.test(moment.dataset.videoId)) return;
        event.preventDefault();
        this.watch(moment, moment.closest('[data-part-card]') || this.activeCard);
      }, options);
      this.querySelector('[data-part-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => {
        this.querySelector('[data-part-player]').replaceChildren();
        this.trigger?.focus({preventScroll: true});
      }, options);
      document.addEventListener('shopify:block:select', event => {
        if (!this.cards.some(card => card.dataset.blockId === event.detail?.blockId)) return;
        this.category = ''; this.expanded = true; this.filter();
      }, options);
      this.filter();
    }
    disconnectedCallback() { this.events?.abort(); this.dialog?.close(); this.querySelector('[data-part-player]')?.replaceChildren(); this.ready = false; }
    filter() {
      const matches = this.cards.filter(card => !this.category || card.dataset.category === this.category);
      const limit = this.mobile.matches ? 3 : 6;
      this.cards.forEach(card => { card.hidden = !matches.includes(card) || (!this.expanded && matches.indexOf(card) >= limit); });
      this.filters.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.category === this.category)));
      this.more.hidden = matches.length <= limit;
      this.more.setAttribute('aria-expanded', String(this.expanded));
      this.more.textContent = this.expanded ? 'Show fewer parts' : `Show all ${matches.length} parts`;
      this.querySelector('[data-part-empty]').hidden = matches.length !== 0;
      this.querySelector('[data-part-results]').textContent = `Showing ${this.expanded ? matches.length : Math.min(limit, matches.length)} of ${matches.length} ${matches.length === 1 ? 'part' : 'parts'}`;
    }
    watch(moment, card) {
      if (!card) return;
      const chapterFocused = this.dialog.contains(document.activeElement);
      if (!this.dialog.open) { this.trigger = moment; this.dialog.showModal(); }
      this.activeCard = card;
      this.querySelector('[data-part-name]').textContent = card.dataset.partTitle;
      this.querySelector('[data-part-stage]').textContent = card.querySelector('.hbr-parts__stage')?.textContent || '';
      this.querySelector('[data-part-note]').textContent = [...card.querySelectorAll('.hbr-parts__description, .hbr-parts__fitment')].map(item => item.textContent).join('\n\n');
      const product = this.querySelector('[data-part-product]');
      const source = card.querySelector('[data-part-shop]');
      product.hidden = !source;
      if (source) product.href = source.href;
      const chapters = [...card.querySelectorAll('[data-part-moment]')].map(link => {
        const clone = link.cloneNode(true);
        if (link.href === moment.href) clone.setAttribute('aria-current', 'true');
        return clone;
      });
      this.querySelector('[data-part-chapters]').replaceChildren(...chapters);
      if (chapterFocused) chapters.find(link => link.hasAttribute('aria-current'))?.focus({preventScroll: true});
      this.querySelector('[data-part-now]').textContent = `${moment.dataset.videoTitle || 'Build video'} · ${moment.querySelector('strong')?.textContent || ''} · ${moment.dataset.momentLabel || 'Watch discussion'}`;
      this.querySelector('[data-part-youtube]').href = moment.href;
      const container = this.querySelector('[data-part-player]');
      let video = container.querySelector('hbr-creator-video');
      if (!video || video.dataset.videoId !== moment.dataset.videoId) {
        video = document.createElement('hbr-creator-video');
        video.className = 'hbr-creator__video';
        video.dataset.videoId = moment.dataset.videoId;
        video.dataset.title = moment.dataset.videoTitle || 'Build video';
        const screen = document.createElement('div'); screen.className = 'hbr-creator__video-screen';
        video.append(screen); container.replaceChildren(video);
      }
      video.playAt(moment.dataset.start);
    }
  });
}
if (!customElements.get('hbr-creator-garage')) {
  customElements.define('hbr-creator-garage', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.events = new AbortController();
      const options = {signal: this.events.signal};
      this.tabs = [...this.querySelectorAll('[data-build-tab]')];
      this.panels = [...this.querySelectorAll('[data-build-panel]')];
      if (!this.tabs.length) return;
      this.list = this.querySelector('[data-build-list]');
      this.list.setAttribute('role', 'tablist');
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', this.panels[i].id);
        this.panels[i].setAttribute('role', 'tabpanel');
        tab.addEventListener('click', event => { event.preventDefault(); this.select(i); }, options);
        tab.addEventListener('keydown', event => {
          const mobile = matchMedia('(max-width: 749px)').matches;
          const delta = {ArrowRight: 1, ArrowLeft: -1, ArrowDown: mobile ? 2 : 1, ArrowUp: mobile ? -2 : -1}[event.key];
          let next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : delta === undefined ? null : (i + delta + this.tabs.length) % this.tabs.length;
          if (next === null) return;
          event.preventDefault(); this.select(next); this.tabs[next].focus();
        }, options);
      });
      this.querySelectorAll('[data-build-go]').forEach(button => button.addEventListener('click', () => {
        this.select((this.current + Number(button.dataset.buildGo) + this.tabs.length) % this.tabs.length);
        this.tabs[this.current].focus({preventScroll: true});
        this.list.scrollIntoView({block: 'nearest', behavior: 'auto'});
      }, options));
      this.onBlockSelect = event => {
        const i = this.panels.findIndex(panel => panel.dataset.blockId === event.detail?.blockId || [...panel.querySelectorAll('[data-part-card]')].some(card => card.dataset.blockId === event.detail?.blockId));
        if (i >= 0) this.select(i);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      const initial = this.panels.findIndex(panel => `#${panel.id}` === location.hash);
      this.select(initial < 0 ? 0 : initial);
      this.classList.add('is-ready');
    }
    disconnectedCallback() { this.events?.abort(); document.removeEventListener('shopify:block:select', this.onBlockSelect); this.ready = false; }
    select(index) {
      this.current = index;
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        this.panels[i].hidden = i !== index;
        if (i !== index) this.panels[i].querySelectorAll('hbr-creator-video').forEach(video => {
          video.pause();
        });
      });
      const previous = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length].dataset.buildName;
      const next = this.tabs[(index + 1) % this.tabs.length].dataset.buildName;
      this.querySelector('[data-build-previous]').textContent = previous;
      this.querySelector('[data-build-next]').textContent = next;
      this.querySelector('[data-build-count]').textContent = `${String(index + 1).padStart(2, '0')} / ${String(this.tabs.length).padStart(2, '0')}`;
    }
  });
}
